import React, { useEffect, useRef } from 'react';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface AdminEmailAnalyticsPageProps {
  period: string;
  analytics: {
    totalEmails: number;
    deliveryRate: number;
    bounceRate: number;
    complaintRate: number;
    emailsByStatus: Record<string, number>;
    emailsByMailer: Record<string, number>;
    bounceBreakdown: Record<string, number>;
    topRecipients: Array<[string, number]>;
    emailsByDay: Record<string, number>;
  };

  // URLs
  emailLogsPath: string;
  analyticsPath: string;

  // Labels
  labels: {
    title: string;
    backLabel: string;
    // Period
    last7Days: string;
    last30Days: string;
    last90Days: string;
    // Stats
    totalEmailsLabel: string;
    deliveryRateLabel: string;
    bounceRateLabel: string;
    complaintRateLabel: string;
    // Sections
    emailsByStatusTitle: string;
    emailsByMailerTitle: string;
    bounceBreakdownTitle: string;
    topRecipientsTitle: string;
    emailsOverTimeTitle: string;
    unknownLabel: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function titleize(str: string | null): string {
  if (!str) return 'Unknown';
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ============================================================================
// Chart Component
// ============================================================================

function EmailsChart({ data }: { data: Record<string, number> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<unknown>(null);

  useEffect(() => {
    const loadChart = async () => {
      // @ts-expect-error Chart.js is loaded globally
      const Chart = window.Chart;
      if (!Chart || !canvasRef.current) return;

      // Destroy existing chart if any
      if (chartRef.current) {
        // @ts-expect-error Chart instance
        chartRef.current.destroy();
      }

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;

      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: Object.keys(data),
          datasets: [
            {
              label: 'Emails Sent',
              data: Object.values(data),
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.1)',
              tension: 0.1,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0,
              },
            },
          },
        },
      });
    };

    loadChart();

    return () => {
      if (chartRef.current) {
        // @ts-expect-error Chart instance
        chartRef.current.destroy();
      }
    };
  }, [data]);

  return <canvas ref={canvasRef} height="80" />;
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminEmailAnalyticsPage({
  period,
  analytics,
  emailLogsPath,
  analyticsPath,
  labels,
}: AdminEmailAnalyticsPageProps) {
  const buildPeriodUrl = (days: number) => `${analyticsPath}?period=${days}`;

  const emailsByMailerSorted = Object.entries(analytics.emailsByMailer).sort(([, a], [, b]) => b - a);

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="h2">{labels.title}</h1>
          <a href={emailLogsPath} className="text-muted">
            ← {labels.backLabel}
          </a>
        </div>
      </div>

      {/* Period Selector */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="btn-group" role="group">
            <a
              href={buildPeriodUrl(7)}
              className={`btn ${period === '7' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              {labels.last7Days}
            </a>
            <a
              href={buildPeriodUrl(30)}
              className={`btn ${period === '30' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              {labels.last30Days}
            </a>
            <a
              href={buildPeriodUrl(90)}
              className={`btn ${period === '90' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              {labels.last90Days}
            </a>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="row mb-4">
        <div className="col-md-3">
          <AdminStatCard title={labels.totalEmailsLabel} value={analytics.totalEmails} />
        </div>
        <div className="col-md-3">
          <AdminStatCard
            title={labels.deliveryRateLabel}
            value={`${analytics.deliveryRate}%`}
            borderColor="success"
            valueColor="success"
          />
        </div>
        <div className="col-md-3">
          <AdminStatCard
            title={labels.bounceRateLabel}
            value={`${analytics.bounceRate}%`}
            borderColor="warning"
            valueColor="warning"
          />
        </div>
        <div className="col-md-3">
          <AdminStatCard
            title={labels.complaintRateLabel}
            value={`${analytics.complaintRate}%`}
            borderColor="danger"
            valueColor="danger"
          />
        </div>
      </div>

      <div className="row">
        {/* Emails by Status */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">{labels.emailsByStatusTitle}</h5>
            </div>
            <div className="card-body">
              <table className="table table-sm">
                <tbody>
                  {Object.entries(analytics.emailsByStatus).map(([status, count]) => (
                    <tr key={status}>
                      <td>{titleize(status)}</td>
                      <td className="text-end">
                        <strong>{count.toLocaleString()}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Emails by Mailer */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">{labels.emailsByMailerTitle}</h5>
            </div>
            <div className="card-body">
              <table className="table table-sm">
                <tbody>
                  {emailsByMailerSorted.map(([mailer, count]) => (
                    <tr key={mailer}>
                      <td>
                        <code>{mailer}</code>
                      </td>
                      <td className="text-end">
                        <strong>{count.toLocaleString()}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Bounce Breakdown */}
        {Object.keys(analytics.bounceBreakdown).length > 0 && (
          <div className="col-md-6 mb-4">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">{labels.bounceBreakdownTitle}</h5>
              </div>
              <div className="card-body">
                <table className="table table-sm">
                  <tbody>
                    {Object.entries(analytics.bounceBreakdown).map(([type, count]) => (
                      <tr key={type || 'unknown'}>
                        <td>{titleize(type) || labels.unknownLabel}</td>
                        <td className="text-end">
                          <strong>{count.toLocaleString()}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Top Recipients */}
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">{labels.topRecipientsTitle}</h5>
            </div>
            <div className="card-body">
              <table className="table table-sm">
                <tbody>
                  {analytics.topRecipients.map(([recipient, count]) => (
                    <tr key={recipient}>
                      <td className="text-truncate" style={{ maxWidth: 250 }}>
                        {recipient}
                      </td>
                      <td className="text-end">
                        <strong>{count.toLocaleString()}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Emails by Day Chart */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">{labels.emailsOverTimeTitle}</h5>
        </div>
        <div className="card-body">
          <EmailsChart data={analytics.emailsByDay} />
        </div>
      </div>
    </div>
  );
}

export default AdminEmailAnalyticsPage;
